import { useCallback, useEffect, useState } from 'react';
import type { AccountView, LifeView, Needs, PlayerSettings, PlayState } from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@london-life/ui/components/tabs';
import { api } from '../lib/api';
import { NeedsTab } from './needs-tab';
import { ProfileTab } from './profile-tab';
import { SettingsTab } from './settings-tab';
import { SkillsTab } from './skills-tab';
import { GoalsTab } from './goals-tab';
import { CareerTab } from './career-tab';
import { PeopleTab } from './people-tab';

export type ProfileTabKey = 'profile' | 'needs' | 'goals' | 'skills' | 'career' | 'people' | 'settings';
const TABS: { key: ProfileTabKey; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'needs', label: 'Needs' },
  { key: 'goals', label: 'Goals' },
  { key: 'skills', label: 'Skills' },
  { key: 'career', label: 'Career' },
  { key: 'people', label: 'People' },
  { key: 'settings', label: 'Settings' },
];

// The player's profile: who they are, how they feel, and their settings. Full screen on phones, a large panel
// on bigger screens. Every change is made by the server; this screen shows the result.
export function ProfileScreen({
  tab,
  onTab,
  life,
  play,
  needs,
  settings,
  onLife,
  onSettings,
  onClose,
  onSignOut,
  onDeleteAccount,
  onCookieSettings,
  onNewLife,
  onMessage,
  onChat,
}: {
  tab: ProfileTabKey;
  onTab: (tab: ProfileTabKey) => void;
  life: LifeView;
  play: PlayState | null;
  needs: Needs | null;
  settings: PlayerSettings;
  onLife: (life: LifeView, message?: string) => void;
  onSettings: (settings: PlayerSettings) => void;
  onClose: () => void;
  onSignOut: () => void;
  onDeleteAccount: (confirmUsername: string) => Promise<void>;
  onCookieSettings: () => void;
  onNewLife: () => Promise<void>;
  // a short note on the map (a reward, a payment); the wallet is refreshed with it
  onMessage: (message: string) => void;
  // open a direct chat from the People tab
  onChat?: (lifeId: string) => void;
}) {
  const [account, setAccount] = useState<AccountView | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const load = useCallback(() => {
    setFailure(null);
    api.account
      .get()
      .then((reply) => setAccount(reply.account))
      .catch((error: unknown) => setFailure(describeError(error)));
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  return (
    <div className="profile-screen" role="dialog" aria-modal="true" aria-labelledby="profile-title">
      <Tabs value={tab} onValueChange={(value) => onTab(value as ProfileTabKey)} className="profile-frame">
        <header className="profile-header">
          <button type="button" className="profile-back" onClick={onClose}>
            ‹ Back to the map
          </button>
          <h2 id="profile-title">
            {life.firstName}
            {account?.username && <small>@{account.username}</small>}
          </h2>
          <button type="button" className="life-close" aria-label="Close profile" onClick={onClose}>
            ×
          </button>
          <TabsList className="profile-tabs" aria-label="Profile sections">
            {TABS.map((item) => (
              <TabsTrigger key={item.key} value={item.key}>
                {item.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </header>
        <div className="profile-body">
          {failure && <ErrorNotice error={failure} onRetry={load} />}
          <TabsContent value="profile">
            {account && <ProfileTab life={life} account={account} onAccount={setAccount} onLife={onLife} />}
          </TabsContent>
          <TabsContent value="needs">
            {play && needs && <NeedsTab life={life} play={play} needs={needs} onLife={onLife} />}
          </TabsContent>
          <TabsContent value="goals">
            <GoalsTab onMessage={onMessage} />
          </TabsContent>
          <TabsContent value="skills">{play && <SkillsTab progress={play.progress} />}</TabsContent>
          <TabsContent value="career">
            {play && (
              <CareerTab
                progress={play.progress}
                earnedTodayPence={play.earnedTodayPence}
                capPence={play.earningCapPence}
              />
            )}
          </TabsContent>
          <TabsContent value="people">
            <PeopleTab placeId={play?.placeId ?? null} onMessage={onMessage} onChat={onChat} />
          </TabsContent>
          <TabsContent value="settings">
            {account && (
              <SettingsTab
                account={account}
                settings={settings}
                onSettings={onSettings}
                onSignOut={onSignOut}
                onDeleteAccount={onDeleteAccount}
                onCookieSettings={onCookieSettings}
                onNewLife={onNewLife}
              />
            )}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
